/**
 * Returns `value` only when it parses as an http(s) URL; otherwise returns
 * null. Used as a guard before rendering server-supplied URLs into `<a href>`
 * sinks (e.g. `paymentLinkUrl` from `myExcessCharges` and `subscribeToPlan`).
 *
 * Without this guard, React 18 lets `javascript:...` URIs through in `href`
 * props (it does not sanitize URI schemes), and the absence of a CSP plus
 * JWTs sitting in `localStorage` means a single user click can exfiltrate
 * both the access and refresh tokens — XSS-VULN-01 and XSS-VULN-02 from the
 * admin pentest revalidate.
 *
 * Accepts only `http:` and `https:` schemes. Anything else (`javascript:`,
 * `data:`, `vbscript:`, file paths, malformed strings, null, undefined)
 * returns null so callers can omit the link or render disabled state.
 */
export const safeHttpUrl = (value: unknown): string | null => {
  if (typeof value !== 'string' || value.length === 0) return null;
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? value : null;
  } catch {
    return null;
  }
};
